/**
 * Map authentication errors (especially raw Supabase AuthError messages) to
 * friendly, security-conscious pt-BR messages.
 *
 * Rules:
 *  - Never echo back which exact field was wrong in a way that leaks account
 *    enumeration (login failures are deliberately generic).
 *  - Never surface internal/detail codes (error.code / status) to users.
 *  - Any unknown error falls back to a generic message.
 */

type AuthErrorLike = {
  message?: string;
  code?: string;
  status?: number;
};

const LOGIN_FAILURE =
  "E-mail ou senha incorretos. Verifique os dados e tente novamente.";

const KNOWN_LOGIN_MESSAGES = new Set([
  "Invalid login credentials",
  "Invalid Login Credentials",
  "Email not confirmed",
  "User already registered",
]);

export function friendlyAuthError(caught: unknown): string {
  const error = (caught ?? {}) as AuthErrorLike;
  const raw = String(error?.message ?? "").trim();

  // Recovery / update-password validation reached us directly as raised Error.
  if (
    raw.includes("pelo menos 8") ||
    raw.includes("não corresponde") ||
    raw.includes("mínimo de 8")
  ) {
    return raw;
  }

  if (raw === "Invalid login credentials" || raw === "Invalid Login Credentials") {
    return LOGIN_FAILURE;
  }

  if (
    raw === "Email not confirmed" ||
    raw === "Email not Confirmed" ||
    raw.toLowerCase().includes("email not confirmed")
  ) {
    return "Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.";
  }

  if (
    raw === "User already registered" ||
    raw.toLowerCase().includes("already registered")
  ) {
    return "Já existe uma conta com este e-mail. Faça login ou use a recuperação de senha.";
  }

  if (
    raw === "Email rate limit exceeded" ||
    raw.toLowerCase().includes("rate limit") ||
    raw.toLowerCase().includes("too many requests")
  ) {
    return "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.";
  }

  if (
    raw === "For security purposes, you can only request this after" ||
    raw.toLowerCase().includes("security purposes")
  ) {
    return "Aguarde um momento antes de solicitar um novo link.";
  }

  if (raw === "Password should be at least 8 characters.") {
    return "A senha deve ter pelo menos 8 caracteres.";
  }

  if (error?.status === 429) {
    return "Muitas tentativas em sequência. Aguarde alguns minutos.";
  }

  // Internal Supabase codes we do not want to leak.
  if (KNOWN_LOGIN_MESSAGES.has(raw)) {
    return LOGIN_FAILURE;
  }

  // Unknown messages fall back to a generic, non-leaking message rather than
  // echoing backend text verbatim.
  return "Não foi possível concluir. Tente novamente.";
}

/**
 * Live, accessible validation used for the password/signup new-password field.
 * Returns null when the value is acceptable for the current mode.
 */
export function passwordFieldHint(
  value: string,
  mode: "login" | "signup" | "forgot" | "recovery",
): string {
  if (mode === "login" || mode === "forgot") return "";
  if (value.length === 0) {
    return "Mínimo de 8 caracteres.";
  }
  if (value.length < 8) {
    return `Faltam ${8 - value.length} caractere${8 - value.length === 1 ? "" : "s"} para o mínimo de 8.`;
  }
  return "Senha com o mínimo necessário. Boa escolha!";
}
